import { ref, type Ref } from 'vue';

import AnalyticsHelper from '@/helpers/analytics';

import type { useCommonStore } from '@/stores/common';
import type { useRewardsStore } from '@/stores/rewards';
import type { useUserStore } from '@/stores/user';

import type { ManageRewardsItem, ManageRewardsRole } from '../types';

type CommonStore = ReturnType<typeof useCommonStore>;
type RewardsStore = ReturnType<typeof useRewardsStore>;
type UserStore = ReturnType<typeof useUserStore>;

export function useManageRewardsFlow(params: {
  role: Ref<ManageRewardsRole | null>;
  commonStore: CommonStore;
  rewardsStore: RewardsStore;
  userStore: UserStore;
  t: (key: string) => string;
}) {
  const isSaving = ref(false);

  const onCloseModal = () => {
    params.commonStore.closeManageRewardsModal();
  };

  const onContactAdmin = () => {
    window.open(
      `mailto:${params.userStore.user?.organization?.notificationEmail}`,
      '_blank'
    );

    if (params.role.value === 'reward-purchased') {
      AnalyticsHelper.trackEvent('ask_support', {
        event_properties: 'reward_requested',
        user_properties: 'helper',
      });
    }
  };

  const onFinalizePurchaseReward = async (reward: ManageRewardsItem) => {
    if (isSaving.value) return;
    isSaving.value = true;
    try {
      if (reward.id) {
        await params.rewardsStore.purchaseReward(reward.id);
      }

      params.commonStore.setManageRewardsModal({
        description: params.t('manageRewardsModal.purchaseModal.description'),
        open: true,
        role: 'reward-purchase-successful',
        title: params.t('manageRewardsModal.purchaseModal.thanks'),
      });
    } finally {
      isSaving.value = false;
    }
  };

  const onPurchaseReward = (reward: ManageRewardsItem) => {
    params.commonStore.setManageRewardsModal({
      open: true,
      role: 'reward-purchase',
      item: reward,
      title: reward.name,
      description: reward.description,
    });

    AnalyticsHelper.trackEvent('buy_reward', {
      user_properties: 'helper',
    });
  };

  return {
    isSaving,
    onCloseModal,
    onContactAdmin,
    onFinalizePurchaseReward,
    onPurchaseReward,
  };
}
